import { PaymentService, PaymentStatusResponseDto, PaymentTrackingStatus, InvoicePayment, TransactionType, TerminalMode } from "@services";
import { makeAutoObservable, runInAction } from 'mobx';
import { L } from '@lib';
import { Utils } from '@utils';

type PaymentSuccessCallback = (paymentData: InvoicePayment) => Promise<void>;

// Statuses after which the API no longer changes the attempt on its own.
const SUCCESS_STATUSES: string[] = [PaymentTrackingStatus.Paid, PaymentTrackingStatus.ManualPaid];
const ERROR_STATUSES: string[] = [PaymentTrackingStatus.Failed, PaymentTrackingStatus.Error, PaymentTrackingStatus.Timeout];

const getErrorMessage = (error: unknown): string | undefined =>
  (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;

class PaymentStore {
  loading: boolean = false;
  isTerminalEnabled: boolean = false;

  // Payment processing properties
  processingPayment: boolean = false;
  processingStatus: PaymentStatusResponseDto | null = null;
  paymentFinished: boolean = false;
  trackingId: string | null = null;
  cardPaymentLoading: boolean = false;
  cashPaymentLoading: boolean = false;
  isProcessingPaymentRef: boolean = false;
  resolvingConfirmation: boolean = false;
  cancellingCardPayment: boolean = false;
  cardCancelRequested: boolean = false;

  // Private properties for intervals
  private pollingTimeout: NodeJS.Timeout | null = null;
  private closeTimeout: NodeJS.Timeout | null = null;
  // Non-observable flag — stops polling loop. Must NOT be tracked by MobX.
  private _pollingCancelled = false;
  // What to do once the attempt is confirmed paid (refresh the invoice / record the refund).
  private _onPaymentSuccess: PaymentSuccessCallback | null = null;
  private _paymentValues: InvoicePayment | null = null;

  constructor() {
    makeAutoObservable<PaymentStore, '_pollingCancelled' | '_onPaymentSuccess' | '_paymentValues' | 'pollingTimeout' | 'closeTimeout'>(this, {
      _pollingCancelled: false,
      _onPaymentSuccess: false,
      _paymentValues: false,
      pollingTimeout: false,
      closeTimeout: false,
    });
  }

  setLoading = (value: boolean) => {
    runInAction(() => {
      this.loading = value;
    });
  };

  setTerminalEnabled = (value: boolean) => {
    runInAction(() => {
      this.isTerminalEnabled = value;
    });
  };

  checkTerminalStatus = async () => {
    this.setLoading(true);
    try {
      const terminalEnabled = await PaymentService.hasTerminalEnabled();
      this.setTerminalEnabled(terminalEnabled);
      return terminalEnabled;
    } catch (error) {
      console.error('Failed to check terminal status:', error);
      this.setTerminalEnabled(false);
      return false;
    } finally {
      this.setLoading(false);
    }
  };

  clearTerminalStatus = () => {
    this.resetPaymentState();
    runInAction(() => {
      this.isTerminalEnabled = false;
      this.loading = false;
    });
  };

  setProcessingPayment = (value: boolean) => {
    runInAction(() => {
      this.processingPayment = value;
    });
  };

  setProcessingStatus = (status: PaymentStatusResponseDto | null) => {
    runInAction(() => {
      this.processingStatus = status;
    });
  };

  setPaymentFinished = (value: boolean) => {
    runInAction(() => {
      this.paymentFinished = value;
    });
  };

  setTrackingId = (id: string | null) => {
    runInAction(() => {
      this.trackingId = id;
    });
  };

  setCardPaymentLoading = (value: boolean) => {
    runInAction(() => {
      this.cardPaymentLoading = value;
    });
  };

  setCashPaymentLoading = (value: boolean) => {
    runInAction(() => {
      this.cashPaymentLoading = value;
    });
  };

  setIsProcessingPaymentRef = (value: boolean) => {
    runInAction(() => {
      this.isProcessingPaymentRef = value;
    });
  };

  get hasPaymentError() {
    return ERROR_STATUSES.includes(this.processingStatus?.status ?? '');
  }

  get signatureRequired(){
    return this.processingStatus?.status === 'SignatureVerificationRequired';
  }

  /** The card machine result is unknown — the operator must confirm it before card can be used again. */
  get requiresConfirmation() {
    return this.processingStatus?.status === PaymentTrackingStatus.Unconfirmed;
  }

  /** Dojo accepted the cancel; the session can still be Captured or Expire, so polling decides the outcome. */
  get cancelRequested() {
    return this.cardCancelRequested || this.processingStatus?.terminalSessionStatus === 'CancelRequested';
  }

  get isReconnecting() {
    return this.processingStatus?.status === PaymentTrackingStatus.Reconnecting;
  }

  /** A card attempt is running on the card machine (not finished, not awaiting confirmation). */
  get hasActiveCardAttempt() {
    return this.processingPayment && !!this.trackingId && this.cardPaymentLoading;
  }

  // Clear all intervals and timeouts
  clearIntervals = () => {
    this._pollingCancelled = true;
    if (this.pollingTimeout) {
      clearTimeout(this.pollingTimeout);
      this.pollingTimeout = null;
    }
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  };

  resetPaymentState = () => {
    this._pollingCancelled = true;
    this._onPaymentSuccess = null;
    this._paymentValues = null;
    runInAction(() => {
      this.processingPayment = false;
      this.processingStatus = null;
      this.paymentFinished = false;
      this.trackingId = null;

      this.cardPaymentLoading = false;
      this.cashPaymentLoading = false;
      this.isProcessingPaymentRef = false;
      this.resolvingConfirmation = false;
      this.cancellingCardPayment = false;
      this.cardCancelRequested = false;
    });
    this.clearIntervals();
  };

  verifySignature = async (trackingId: string, isValid: boolean) => {
    try {
      return await PaymentService.verifySignature({ trackingId, isValid });
    } catch (error) {
      // The attempt keeps running on the card machine (e.g. the 80 s signature window already accepted it);
      // polling reports the result, so the state is not reset here.
      console.error('Signature verification failed:', error);
      return undefined;
    }
  };

  processCardPayment = async (
    invoiceId: string, 
    amount: number, 
    terminalMode: TerminalMode,
    onPaymentSuccess: PaymentSuccessCallback
  ) => {
    // Never abandon an attempt that is still running on the card machine.
    if (this.hasActiveCardAttempt || this.requiresConfirmation) return;

    this.resetPaymentState();
    this.setCardPaymentLoading(true);
    this.setProcessingStatus(null);
    
    try {
      const roundedAmount = Utils.roundToTwoDecimals(amount);

      //investigate why it fails with InvoicePayment type
      const originalPaymentValues: any = {
          amount: roundedAmount,
          method: terminalMode === TerminalMode.Sale ? 'Card' : 'CardRefund',
          transactionType: TransactionType.Payment,
          invoiceId: invoiceId,
      };

      this.setIsProcessingPaymentRef(false);
      this.setProcessingPayment(true);
      this.setProcessingStatus({
        status: 'INITIALIZING',
        statusMessage: L('Initializing payment...'),
        order: 1,
      });
      this.setPaymentFinished(false);

      // Create tracking session (the API rejects it while another attempt is running or unconfirmed)
      let trackingResponse;
      try {
        trackingResponse = await PaymentService.createPaymentTrackingSession({
          invoiceId,
          amount: roundedAmount
        });
      } catch (error) {
        // Pick up the attempt that blocked this one, e.g. an unconfirmed result from another tab.
        if (await this.resumeActiveAttempt(invoiceId, onPaymentSuccess, true)) return;
        throw error;
      }

      this.setTrackingId(trackingResponse.id);

      // Start payment processing (don't await)
      PaymentService.processPayment({
        invoiceId,
        amount: roundedAmount,
        paymentTrackingId: trackingResponse.id,
        terminalMode: terminalMode
      }).catch((error) => console.error('Process payment request failed:', error));

      // Start polling for status
      this.startStatusPolling(trackingResponse.id, originalPaymentValues, onPaymentSuccess);

    } catch (error) {
      console.error("Payment initialization error:", error);
      this.setProcessingStatus({
        status: 'ERROR',
        statusMessage: getErrorMessage(error) || L('Payment initialization failed. Please try again.'),
        order: this.processingStatus?.order || 1
      });
      this.setIsProcessingPaymentRef(false);
      this.setCardPaymentLoading(false);
      setTimeout(() => this.setProcessingPayment(false), 3000);
      setTimeout(() => this.resetPaymentState(), 3000);
    }
  };

  /**
   * Re-attaches to a card attempt for this invoice that is still running or awaiting confirmation
   * (modal reopened, page reloaded). Returns true when one was found.
   */
  resumeActiveAttempt = async (
    invoiceId: string,
    onPaymentSuccess: PaymentSuccessCallback,
    force = false
  ): Promise<boolean> => {
    if (!force && (this.processingPayment || this.trackingId)) return false;

    let attempt: PaymentStatusResponseDto | null = null;
    try {
      attempt = await PaymentService.getActivePaymentAttempt(invoiceId);
    } catch (error) {
      console.error('Could not load the active card attempt:', error);
      return false;
    }

    if (!attempt?.trackingId) return false;

    // A new attempt may have started while the lookup was in flight.
    if (!force && (this.processingPayment || this.trackingId)) return false;

    const isRefund = attempt.sessionType === 'UnlinkedRefund';
    const paymentValues: any = {
      amount: Utils.roundToTwoDecimals(attempt.amount ?? 0),
      method: isRefund ? 'CardRefund' : 'Card',
      transactionType: TransactionType.Payment,
      invoiceId,
    };

    this.clearIntervals();
    this._onPaymentSuccess = onPaymentSuccess;
    this._paymentValues = paymentValues;

    runInAction(() => {
      this.trackingId = attempt!.trackingId!;
      this.processingStatus = attempt;
      this.processingPayment = true;
      this.paymentFinished = false;
      this.cardPaymentLoading = !attempt!.requiresConfirmation;
    });

    if (!attempt.requiresConfirmation) {
      this.startStatusPolling(attempt.trackingId, paymentValues, onPaymentSuccess);
    }

    return true;
  };

  /** Operator's answer after checking the card machine screen / receipt for an UNCONFIRMED result. */
  resolveUnconfirmed = async (paidOnTerminal: boolean) => {
    if (!this.trackingId || this.resolvingConfirmation) return;

    runInAction(() => {
      this.resolvingConfirmation = true;
    });

    try {
      const result = await PaymentService.resolveUnconfirmedPayment({
        trackingId: this.trackingId,
        paidOnTerminal,
      });

      this.setProcessingStatus(result);

      if (SUCCESS_STATUSES.includes(result.status)) {
        await this.completeSuccessfulAttempt();
        return;
      }

      // Not paid: clear the attempt so the card payment can be re-tried.
      this.resetPaymentState();
    } catch (error) {
      console.error('Could not resolve the card payment:', error);
    } finally {
      runInAction(() => {
        this.resolvingConfirmation = false;
      });
    }
  };

  /** Cancels the running session on the card machine (only possible before a card is presented). */
  cancelCardPayment = async (): Promise<{ success: boolean; message?: string }> => {
    if (!this.trackingId || this.cancellingCardPayment) return { success: false };

    runInAction(() => {
      this.cancellingCardPayment = true;
    });

    try {
      const result = await PaymentService.cancelTerminalPayment({ trackingId: this.trackingId });
      if (result.success) {
        runInAction(() => {
          this.cardCancelRequested = true;
        });
      }
      return { success: result.success, message: result.message };
    } catch (error) {
      return { success: false, message: getErrorMessage(error) };
    } finally {
      runInAction(() => {
        this.cancellingCardPayment = false;
      });
    }
  };

  private completeSuccessfulAttempt = async () => {
    this._pollingCancelled = true;
    this.setIsProcessingPaymentRef(true);
    this.setPaymentFinished(true);
    this.setCardPaymentLoading(false);

    const onSuccess = this._onPaymentSuccess;
    const values = this._paymentValues;
    if (onSuccess && values) {
      try {
        await onSuccess(values);
      } catch (error) {
        console.error('Error processing payment success:', error);
      }
    }

    this.closeTimeout = setTimeout(() => {
      this.setProcessingPayment(false);
      this.setPaymentFinished(false);
      this.setIsProcessingPaymentRef(false);
      this.setTrackingId(null);
    }, 5000);
  };

  // Sequential status polling — only ONE request in-flight at a time.
  // Eliminates the race condition caused by setInterval stacking requests on slow networks.
  private startStatusPolling = (
    trackingId: string, 
    originalPaymentValues: InvoicePayment, 
    onPaymentSuccess: PaymentSuccessCallback
  ) => {
    this._pollingCancelled = false;
    this._onPaymentSuccess = onPaymentSuccess;
    this._paymentValues = originalPaymentValues;

    const pollOnce = async () => {
      if (this._pollingCancelled) return;

      try {
        const statusResult = await PaymentService.getPaymentStatus(trackingId);

        // Check cancellation again after await (could have been cancelled while waiting)
        if (this._pollingCancelled) return;

        this.setProcessingStatus(statusResult);

        if (SUCCESS_STATUSES.includes(statusResult.status)) {
          await this.completeSuccessfulAttempt();
          return; // Done — do not schedule next poll
        }

        if (statusResult.status === PaymentTrackingStatus.Unconfirmed) {
          // Result unknown: stop polling and ask the operator to check the card machine.
          this._pollingCancelled = true;
          this.setIsProcessingPaymentRef(false);
          this.setCardPaymentLoading(false);
          return;
        }

        if (ERROR_STATUSES.includes(statusResult.status) || statusResult.status === PaymentTrackingStatus.ManualFailed) {
          this._pollingCancelled = true;
          this.setIsProcessingPaymentRef(false);
          this.setCardPaymentLoading(false);
          this.setProcessingStatus({
            status: statusResult.status,
            statusMessage: statusResult.statusMessage || L("Payment failed"),
            order: statusResult.order
          });
          this.setPaymentFinished(true);
          return; // Done — do not schedule next poll
        }

        // Still processing — schedule next poll (only after current one completes)
        if (!this._pollingCancelled) {
          this.pollingTimeout = setTimeout(pollOnce, 500);
        }
      } catch (error) {
        console.error("Error polling payment status:", error);
        // On network error, retry after delay unless cancelled
        if (!this._pollingCancelled) {
          this.pollingTimeout = setTimeout(pollOnce, 1000);
        }
      }
    };

    // Start first poll
    pollOnce();
  };

  // Cleanup method
  cleanup = () => {
    this.clearIntervals();
    this.resetPaymentState();
  };
}

export default PaymentStore;
